import { useEffect, useMemo, useState } from 'react';
import {
	useConsentBannerUserPreferencesResource,
	type JiraPrefetchedConsentPreferenceData,
} from '@jira/router-resources__cookie-consent/src/useConsentBannerUserPreferencesResource.tsx';
import { useIsClientReady } from '@jira/ssr__ssr-toolkit/src/hooks/use-is-client-ready.tsx';
import { getCookie } from '@jira/platform__browser-storage-controls/src/utils/get-cookie/index.tsx';
import { shouldRenderBannerPrefetched } from '@atlassian/browser-storage-controls/controllers/banner-controls/should-banner-render-prefetched';
import { fg } from '@atlassian/jira-feature-gating';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { BANNER_NAME } from './bannerName.constants.tsx';
import {
	useIsInIframe,
	useCookieConsentBannerSettings,
} from './useCookieConsentBannerSettings.tsx';

const getCookieSSR = (name: string): string | undefined => {
	return getCookie(name, true);
};

export const shouldRenderBanner = (
	data: JiraPrefetchedConsentPreferenceData | null,
	isClientReady: boolean,
): boolean | null => {
	try {
		/**
		 * If we are using the cookie data to verify if we show the banner, and we don't use ATLASSIAN_SHOW_BANNER_COOKIE_KEY
		 * we end up in a situation where the banner is shown,
		 * then the client renders ConsentBanner from '@atlassian/browser-storage-controls'
		 * which then checks that cookie and if it's false
		 * and then we dismiss the banner anyway
		 *
		 * This is because the cookie consent logic does not set the 'fallback' cookie when we prefetch the data
		 * Because of this, shouldRenderBannerPrefetched will always end up returning true if we prefetch
		 *
		 * If we look at the getShowBanner code platform/packages/policy-platform/browser-storage-controls/src/ui/consent-banner/use-banner-visibility/index.ts
		 * We can see that it will use this show banner cookie to determine if the banner should be shown
		 * as long as we aren't in localConsentMode (which we aren't since we do not pass localConsentMode=true to ConsentBanner
		 * in jira/src/packages/platform/banners-cookie-consent/src/ui/banner/CookieConsentBanner.tsx)
		 *
		 * So we can use the ATLASSIAN_SHOW_BANNER_COOKIE_KEY to determine if the banner should be shown instead if it is set
		 */
		if (data?.shouldShowBanner !== undefined) {
			return data.shouldShowBanner;
		}
		if (fg('fedramp-jira-banner-fix')) {
			return (
				data === null ||
				shouldRenderBannerPrefetched(data, {
					isSSR: !isClientReady,
					getCookie: getCookieSSR,
				})
			);
		}
		return (
			data &&
			shouldRenderBannerPrefetched(data, {
				isSSR: !isClientReady,
				getCookie: getCookieSSR,
			})
		);
	} catch (e) {
		return true;
	}
};

export const useBannerCookieConsent = (): {
	isEnabled: boolean;
	name: string;
	height: number;
} => {
	const isClientReady = useIsClientReady();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [{ isDismissed, height, hasInit }, { onShow, init }] = useCookieConsentBannerSettings();

	const { data, loading, error } = useConsentBannerUserPreferencesResource();

	const isIframe: boolean = useIsInIframe();

	// This is the initial state of the banner esp in ssr land
	const [initialShouldShow] = useState(() => {
		if (loading) {
			return false;
		}
		if (error) {
			return true;
		}
		return Boolean(shouldRenderBanner(data, isClientReady) && !isIframe);
	});

	const shouldShow = useMemo(
		() => (hasInit ? !isDismissed : initialShouldShow),
		[hasInit, initialShouldShow, isDismissed],
	);

	useEffect(() => {
		if (hasInit || loading) {
			return;
		}
		// Now that we're in the client render, let's re-verify if we should show the banner
		// Eg to verify if we are in an iframe
		const shouldRender = Boolean(error || (shouldRenderBanner(data, true) && !isIframe));
		init(!shouldRender);

		// We should verify how often we end up having a mismatch between the initial render (probably ssr) and init render (client)
		if (!fg('killswitch-analytics-ssr-cookie-consent-banner')) {
			if (initialShouldShow !== shouldRender) {
				fireOperationalAnalytics(
					createAnalyticsEvent({}),
					'cookieConsentBanner showBannerMismatch',
					{
						isInIframe: isIframe,
						initialShouldShow,
						initShouldShow: shouldRender,
					},
				);
			}
		}
	}, [
		createAnalyticsEvent,
		data,
		error,
		hasInit,
		init,
		initialShouldShow,
		isIframe,
		loading,
		onShow,
	]);

	return useMemo(
		() => ({
			isEnabled: shouldShow,
			name: BANNER_NAME,
			height,
		}),
		[shouldShow, height],
	);
};
